<template>
    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Most recent revenue"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata &&
                    yieldMetadata['last_valid_row'] &&
                    yieldMetadata['last_valid_row']['publisher revenue'] > 0
                        ? numberFormatWithSpaceRound(
                              yieldMetadata['last_valid_row']['publisher revenue'] / exchangeRate
                          ) +
                          ' ' +
                          currencySymbol
                        : 'N/A'
                "
                color="success"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="Revenue for the selected period"
                :loading="yieldLoading"
                :value="numberFormatWithSpaceRound(revenueSelectedPeriod)"
                :postfix="' ' + currencySymbol"
                color="success"
            />
        </div>
    </div>

    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Impressions for the selected period"
                :loading="yieldLoading"
                :value="numberFormat(impressionsSelectedPeriod, 3)"
            />
        </div>

        <div class="column">
            <SimpleCard
                data-test="card"
                title="RPM for the selected period"
                :loading="yieldLoading"
                :value="rpmSelectedPeriod"
                :postfix="' ' + currencySymbol"
                :is-decimal="true"
            />
        </div>
    </div>

    <div class="columns">
        <div class="column">
            <SimpleCard
                data-test="card"
                title="Most recent impressions"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata &&
                    yieldMetadata['last_valid_row'] &&
                    yieldMetadata['last_valid_row']['impressions'] > 0
                        ? numberFormat(yieldMetadata['last_valid_row']['impressions'], 3)
                        : 'N/A'
                "
            />
        </div>

        <div class="column">
            <SimpleCard
                :without-format-cell="true"
                data-test="card"
                title="Most recent RPM"
                :loading="yieldLoading"
                :title_2="
                    yieldMetadata && yieldMetadata['last_valid_row']
                        ? formatDate(makeDateObject(yieldMetadata['last_valid_row']['date']))
                        : ''
                "
                :value="
                    yieldMetadata && yieldMetadata['last_valid_row'] && yieldMetadata['last_valid_row']['rpm'] > 0
                        ? numberFormatWithSpaceRound(yieldMetadata['last_valid_row']['rpm'] / exchangeRate) +
                          ' ' +
                          currencySymbol
                        : 'N/A'
                "
                :is-decimal="rpmYesterday > 0"
            />
        </div>
    </div>

    <div class="columns" v-if="yieldData?.length">
        <div class="column">
            <LaMultiPublisherYieldDataCard
                :yield-data="yieldData"
                :yield-metadata="yieldMetadata"
                :yield-csv-data="yieldCsvData"
                :yield-loading="yieldLoading"
                :publishers="publisherNames"
            />
        </div>
    </div>
    <div class="columns" v-else-if="yieldLoading">
        <div class="column">
            <LaDataCardLoader card :chart-count="2" :chart-height="500" />
        </div>
    </div>

    <LaNoData
        v-if="!yieldMetadata && !yieldLoading"
        description="No data available for the selected publishers and period."
    />
</template>

<script>
import LaNoData from "@/components/NoData";
import LaDataCardLoader from "@/components/loaders/DataCardLoader";
import LaMultiPublisherYieldDataCard from "@/components/multiPublisher/MultiPublisherYieldDataCard.vue";
import { getExchangeRateFromCzk, getCurrencySymbol, numberFormat, numberFormatWithSpaceRound } from "@/helpers/data";
import { formatDate } from "@/helpers/filters.js";
import store from "@/store/store";
import { ref, computed, watch } from "vue";
import {
    getterTypes as multiPublisherYieldGetterTypes,
    actionTypes as multiPublisherYieldActionTypes,
} from "@/store/MultiPublisherYield/types";
import { getterTypes as publisherGetterTypes, actionTypes as publisherActionTypes } from "@/store/Publisher/types";
import SimpleCard from "@/components/SimpleCard";

export default {
    name: "LaMultiPublisherYieldCards",
    methods: {
        numberFormat,
        numberFormatWithSpaceRound,
        makeDateObject(yyyy_mm_dd) {
            return new Date(yyyy_mm_dd);
        },
        formatDate,
        getExchangeRateFromCzk,
        getCurrencySymbol,
    },
    components: {
        SimpleCard,
        LaDataCardLoader,
        LaNoData,
        LaMultiPublisherYieldDataCard,
    },
    props: {
        payload: {
            type: Object,
            required: true,
        },
        publishers: {
            required: true,
            type: Array,
        },
    },
    setup(props) {
        const allPublishers = computed(() => store.getters[publisherGetterTypes.getPublishers] || []);
        const selectCurrency = computed(() => store.getters[publisherGetterTypes.getSelectedPublisherCurrency]);
        const exchangeRate = computed(() => getExchangeRateFromCzk(selectCurrency.value));
        const currencySymbol = computed(() => getCurrencySymbol(selectCurrency.value));
        const publisherNames = computed(() => props.publishers?.map(({ name }) => name) || []);
        const yieldLoading = computed(() => store.getters[multiPublisherYieldGetterTypes.isLoading]);
        const loading = computed(() => yieldLoading.value);

        const revenueSelectedPeriod = ref(0);
        const impressionsSelectedPeriod = ref(0);
        const rpmSelectedPeriod = ref(0);

        const yieldData = computed(() => {
            const data = store.getters[multiPublisherYieldGetterTypes.getYieldData];
            if (!data) return null;
            // Transform data to add publisher name
            return data.map((item) => ({
                ...item,
                publisher:
                    allPublishers.value.find((p) => p.id === item.publisher_id)?.name ||
                    `Publisher ${item.publisher_id}`,
            }));
        });

        const yieldMetadata = computed(() => store.getters[multiPublisherYieldGetterTypes.getYieldMetadata]);

        const yieldCsvData = computed(() => {
            const csv = store.getters[multiPublisherYieldGetterTypes.getYieldCsvData];
            if (!csv) return null;

            // CSV data doesn't contain publisher_id - it's aggregated data for all selected publishers
            const selectedPublisherNames = props.publishers?.map((p) => p.name).join(", ") || "Multiple Publishers";

            // Transform csv data to add publishers field (always plural, consistent naming)
            return csv.map((item) => ({
                ...item,
                publishers: selectedPublisherNames,
            }));
        });

        const rpmYesterday = computed(() => {
            return yieldMetadata.value &&
                yieldMetadata.value["last_valid_row"] &&
                yieldMetadata.value["last_valid_row"]["rpm"]
                ? yieldMetadata.value["last_valid_row"]["rpm"] / exchangeRate.value
                : 0;
        });

        const fetchData = (payload) => {
            const publisherIds = props.publishers?.map((p) => p.id) || [];
            if (publisherIds.length === 0) {
                return;
            }

            // Ensure publishers are loaded before fetching yield data
            if (!allPublishers.value || allPublishers.value.length === 0) {
                store.dispatch(publisherActionTypes.getData).then(() => {
                    fetchYieldData(payload, publisherIds);
                });
            } else {
                fetchYieldData(payload, publisherIds);
            }
        };

        const fetchYieldData = (payload, publisherIds) => {
            store
                .dispatch(multiPublisherYieldActionTypes.getData, {
                    period: payload.period,
                    publisherIds: publisherIds,
                    timezone: payload.timezone,
                })
                .then((data) => {
                    revenueSelectedPeriod.value = (data?.metadata?.["total_revenue"] || 0) / exchangeRate.value;
                    rpmSelectedPeriod.value = (data?.metadata?.["total_rpm"] || 0) / exchangeRate.value;
                    impressionsSelectedPeriod.value = data?.metadata?.["total_impressions"] || 0;
                });
        };

        watch(
            () => [props.payload, props.publishers],
            () => {
                if (props.payload && props.publishers?.length > 0) {
                    fetchData(props.payload);
                }
            },
            { immediate: true, deep: true }
        );

        return {
            exchangeRate,
            loading,
            publisherNames,
            currencySymbol,
            yieldData,
            yieldMetadata,
            yieldCsvData,
            yieldLoading,
            revenueSelectedPeriod,
            impressionsSelectedPeriod,
            rpmSelectedPeriod,
            rpmYesterday,
        };
    },
};
</script>

<style lang="scss" scoped>
.small-label {
    font-size: 0.8rem;
}
</style>
